import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import { CultivarAreaForm, OrchardMap, PlantingPlanEditor } from '../../components/orchard';
import { Sprout, Loader2 } from 'lucide-react';
import orchardService from '../../services/orchard';
import type { CultivarArea, Block, Farm, GeoJSONPolygon, PlantingPlan } from '../../types/orchard';
import { toNum } from '@/utils/numeric';

function CultivarAreaEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [area, setArea] = useState<CultivarArea | null>(null);
  const [block, setBlock] = useState<Block | null>(null);
  const [farm, setFarm] = useState<Farm | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [polygon, setPolygon] = useState<GeoJSONPolygon | null>(null);
  const [areaFromMap, setAreaFromMap] = useState<number | null>(null);

  // Planting-plan map interaction state
  const [drawMode, setDrawMode] = useState<'cultivar-area' | 'first-row-line'>('cultivar-area');
  const [pendingFirstRowLine, setPendingFirstRowLine] = useState<{
    start: [number, number];
    end: [number, number];
  } | null>(null);
  const [plantingPlan, setPlantingPlan] = useState<PlantingPlan | null>(null);

  const areaId = Number(id);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const areaResponse = await orchardService.getCultivarArea(areaId);
      if (!areaResponse.success || !areaResponse.data) {
        setError('Failed to load cultivar area');
        return;
      }
      const areaData = areaResponse.data;
      setArea(areaData);
      setPolygon(areaData.boundary_geometry);
      setAreaFromMap(toNum(areaData.area_hectares));

      const [blockResponse, farmResponse] = await Promise.all([
        orchardService.getBlock(areaData.block_id),
        orchardService.getFarm(areaData.farm_id),
      ]);

      if (blockResponse.success && blockResponse.data) {
        setBlock(blockResponse.data);
      }
      if (farmResponse.success && farmResponse.data) {
        setFarm(farmResponse.data);
      }
    } catch {
      setError('Failed to load area data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [areaId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // "Lay out rows" on the detail page links to #rows; the router does not
  // scroll to a hash on its own, and the section is below a tall map.
  const { hash } = useLocation();
  useEffect(() => {
    if (!loading && hash === '#rows') {
      document.getElementById('rows')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [loading, hash]);

  const handlePolygonCreated = useCallback(async (newPolygon: GeoJSONPolygon) => {
    setPolygon(newPolygon);
    try {
      const response = await orchardService.calculateArea(newPolygon);
      if (response.success && response.data) {
        setAreaFromMap(response.data.hectares);
      }
    } catch {
      setAreaFromMap(null);
    }
  }, []);

  const handlePolygonEdited = useCallback(async (editedPolygon: GeoJSONPolygon) => {
    setPolygon(editedPolygon);
    try {
      const response = await orchardService.calculateArea(editedPolygon);
      if (response.success && response.data) {
        setAreaFromMap(response.data.hectares);
      }
    } catch {
      setAreaFromMap(null);
    }
  }, []);

  const handleSubmit = async (fieldData: {
    name: string;
    cultivar_id: number;
    area_hectares?: number;
    tree_count?: number;
    row_spacing_meters?: number;
    tree_spacing_meters?: number;
    planting_date?: string;
    rootstock?: string;
    rootstock_cultivar_id?: number | null;
    interstem_cultivar_id?: number | null;
    graft_union_height_mm?: number | null;
    irrigation_zone?: string;
    training_system?: string;
    tree_supplier?: string;
    notes?: string;
    active?: boolean;
  }) => {
    if (!polygon || !area) return;
    // Planted by rows: the saved rows own tree count and spacing. Sending the
    // form's copies would overwrite them with whatever the hidden fields held.
    const byRows = Boolean(area.first_row_start_point && area.first_row_end_point);

    setIsSubmitting(true);
    setError(null);

    const centerPoint = orchardService.calculateCenterPoint(polygon.coordinates);

    try {
      const response = await orchardService.updateCultivarArea(areaId, {
        block_id: area.block_id,
        farm_id: area.farm_id,
        name: fieldData.name,
        cultivar_id: fieldData.cultivar_id,
        boundary_geometry: polygon,
        center_point: centerPoint,
        area_hectares: fieldData.area_hectares ?? areaFromMap ?? undefined,
        tree_count: byRows ? undefined : fieldData.tree_count,
        row_spacing_meters: byRows ? undefined : fieldData.row_spacing_meters,
        tree_spacing_meters: byRows ? undefined : fieldData.tree_spacing_meters,
        planting_date: fieldData.planting_date,
        rootstock: fieldData.rootstock,
        rootstock_cultivar_id: fieldData.rootstock_cultivar_id ?? null,
        interstem_cultivar_id: fieldData.interstem_cultivar_id ?? null,
        graft_union_height_mm: fieldData.graft_union_height_mm ?? null,
        irrigation_zone: fieldData.irrigation_zone,
        training_system: fieldData.training_system,
        tree_supplier: fieldData.tree_supplier,
        notes: fieldData.notes,
        active: fieldData.active,
      });

      if (response.success) {
        navigate(`/orchard/cultivar-areas/${areaId}`);
      } else {
        setError((response as any).message || 'Failed to update cultivar area');
      }
    } catch {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader
          title="Edit Cultivar Area"
          subtitle="Loading..."
          icon={<Sprout className="h-6 w-6 text-white" />}
          showBackButton
        />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-10 w-10 text-primary-600 animate-spin" />
        </div>
      </div>
    );
  }

  if (error && !area) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader
          title="Edit Cultivar Area"
          subtitle="Error"
          icon={<Sprout className="h-6 w-6 text-white" />}
          showBackButton
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-error-50 border border-error-200 rounded-xl p-6 text-center">
            <p className="text-error-700 mb-4">{error}</p>
            <Button variant="outline" onClick={loadData}>Try Again</Button>
          </div>
        </div>
      </div>
    );
  }

  const contextBlocks = block ? [block] : [];

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title={`Edit: ${area?.name || 'Cultivar Area'}`}
        subtitle={
          area?.cultivar_name
            ? `Cultivar: ${area.cultivar_name}`
            : 'Update cultivar area details'
        }
        icon={<Sprout className="h-6 w-6 text-white" />}
        showBackButton
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 bg-error-50 border border-error-200 rounded-lg p-4">
            <p className="text-error-700">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Form */}
          <Card>
            <CardContent className="p-6">
              <h2 className="text-base font-semibold text-neutral-900 mb-4">Cultivar Area Details</h2>
              {area && (
                <CultivarAreaForm
                  initialValues={{
                    name: area.name,
                    cultivar_id: area.cultivar_id ?? undefined,
                    area_hectares: toNum(area.area_hectares) ?? undefined,
                    tree_count: area.tree_count ?? undefined,
                    row_spacing_meters: toNum(area.row_spacing_meters) ?? undefined,
                    tree_spacing_meters: toNum(area.tree_spacing_meters) ?? undefined,
                    planting_date: area.planting_date ?? undefined,
                    rootstock: area.rootstock ?? undefined,
                    rootstock_cultivar_id: area.rootstock_cultivar_id ?? null,
                    interstem_cultivar_id: area.interstem_cultivar_id ?? null,
                    graft_union_height_mm: area.graft_union_height_mm ?? null,
                    irrigation_zone: area.irrigation_zone ?? undefined,
                    training_system: area.training_system ?? undefined,
                    tree_supplier: area.tree_supplier ?? undefined,
                    notes: area.notes ?? undefined,
                    active: area.active,
                  }}
                  initialRootstockName={area.rootstock_cultivar_name}
                  initialInterstemName={area.interstem_cultivar_name}
                  areaFromMap={areaFromMap}
                  hasPolygon={!!polygon}
                  plantedBy={area.first_row_start_point && area.first_row_end_point ? 'rows' : 'estimate'}
                  rowTreeCount={area.tree_count}
                  onSubmit={handleSubmit}
                  onCancel={() => navigate(`/orchard/cultivar-areas/${areaId}`)}
                  isSubmitting={isSubmitting}
                />
              )}
            </CardContent>
          </Card>

          {/* Map */}
          <div className="space-y-3">
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
              <p className="text-sm text-blue-800 font-medium">
                {drawMode === 'first-row-line'
                  ? 'Click the START of Row 1, then click the END of Row 1. Press Esc to cancel.'
                  : 'Green = farm, Blue = block, Purple = cultivar area, Orange = row 1. Draw a new polygon to replace the boundary.'}
              </p>
            </div>
            <div className="rounded-xl overflow-hidden border border-neutral-200 shadow-sm" style={{ height: '560px' }}>
              <OrchardMap
                farmBoundary={farm?.boundary_geometry}
                blocks={contextBlocks}
                cultivarAreas={area ? [{ ...area, boundary_geometry: polygon ?? area.boundary_geometry }] : []}
                drawMode={drawMode}
                onPolygonCreated={handlePolygonCreated}
                onPolygonEdited={handlePolygonEdited}
                firstRowLine={
                  plantingPlan?.plan.first_row_start_point && plantingPlan?.plan.first_row_end_point
                    ? {
                        start: plantingPlan.plan.first_row_start_point,
                        end: plantingPlan.plan.first_row_end_point,
                      }
                    : undefined
                }
                onFirstRowLineCreated={(line) => {
                  setPendingFirstRowLine(line);
                  setDrawMode('cultivar-area');
                }}
                generatedRows={plantingPlan?.rows}
                selectedEntityId={area?.id}
                selectedEntityType="cultivar-area"
                center={
                  area?.center_point
                    ? [area.center_point.lat, area.center_point.lng]
                    : block?.center_point
                    ? [block.center_point.lat, block.center_point.lng]
                    : undefined
                }
                zoom={17}
              />
            </div>
          </div>
        </div>

        {/* Rows — direction, spacing, pattern, pollinators, per-row changes */}
        {area && (
          <div className="mt-6" id="rows">
            <div className="mb-3">
              <h2 className="text-lg font-semibold text-neutral-900">Rows</h2>
              <p className="text-xs text-neutral-500">
                Lay the planting out in rows to count trees row by row and record which cultivar
                is where. Saved separately from the details above.
              </p>
            </div>
            <PlantingPlanEditor
              area={area}
              drawingFirstRow={drawMode === 'first-row-line'}
              onRequestDrawFirstRow={() => setDrawMode('first-row-line')}
              onCancelDrawFirstRow={() => setDrawMode('cultivar-area')}
              pendingFirstRowLine={pendingFirstRowLine}
              onFirstRowLineConsumed={() => setPendingFirstRowLine(null)}
              onPlanChange={setPlantingPlan}
              onSaved={async () => {
                // Tree count and spacing now come from the rows; refresh the
                // area without remounting the editor.
                const res = await orchardService.getCultivarArea(areaId);
                if (res.success && res.data) setArea(res.data);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default CultivarAreaEdit;
